/**
 * Model used exclusively during signup and verification flow.
 * This is mainl to encapsulate signup and verif-specific 
 * functionality.
 */
import _ from 'underscore';
import jQuery from 'jquery';
import Api from '@/lib/api/api';
import v5_Model from '@/models/v5/model';
export default v5_Model.extend({
    defaults: {
        username: '',
        given_name: '',
        family_name: '',
        country: '',
        password: '',
    },
    /**
     * Sign up for an account.
     *
     * Set the email on the account first; it's required for the API call
     *
     * @return jquery.Deferred
     */
    signup: function() {
        return Api.ajax_v5({
            method: 'POST',
            url: Api.urls.auth_signup(),
            data: JSON.stringify({
                data: { 
                    type: 'account',
                    attributes: { email: this.get('email') } 
                }
            })
        });
    },
    /**
     * Return a jquery deferred, return argument is an object which looks
     * like this:
     *
     * {
     *     account: Account
     *     token: {
     *          access_token: Token,
     *          expires_at: Date
     *          expires_in: 3600
     *          refresh_token: Token
     */
    verify: function() {
        var attrs = _(this.toJSON()).pick([
            'username',
            'given_name',
            'family_name',
            'country',
            'password',
            'accept_terms',
        ]);
        // Required because it's stringy otherwise
        attrs.accept_terms = !!attrs.accept_terms;
        return Api.ajax_v5({
            method: 'POST',
            url: Api.urls.auth_verify_signup(),
            headers: {
                'X-Electricimp-Token': decodeURIComponent(this.get('token'))
            },
            data: JSON.stringify({
                data: {
                    type: 'account',
                    attributes: attrs
                }
            })
        });
    },
    /**
     * Do the first request, attempting to POST to the verify endpoint
     * with only a blank country. This will get us an error response
     * which will include a list of valid country names. We will use this
     * list to create a dropdown menu, and we will use the list of
     * password errors to display password rules to the user.
     *
     * In this context, 'failure' eg a 400 error can be considered a
     * success, because it tells us the legal countr names, a suggested
     * country, and an arra of password requirements.
     *
     * @return a Deferred object
     *
     * In the 'done' state you should get an object that looks like
     *     {
     *         suggested_country: data.meta.suggested_country,
     *         valid_countries: valid_countries,
     *         password_requirements: data.meta.password_requirements
     *     }
     */
    get_verification_params: function() {
        var deferred = new jQuery.Deferred();
        function handle_success() {
            return deferred.fail(Error("Unexpected API response"));
        }
        function handle_error(resp) {
            var data = resp && resp.responseJSON;
            // Check to see if the response is what expect; 
            // without these key fields there's nothing we can do here
            var expected_response = (
                data
            && data.errors
            && data.meta
            && data.meta.password_requirements
            && data.meta.suggested_country
            );
            // Check for Missing Token
            // If we're missing a token in the request, that's a different 
            // kind of error from missing fields.
            var missing_token = _(data.errors).find(function(e) {
                // error if we get a detail of VX016, this is a token issue
                return e.code == 'VX016';
            });
            if (missing_token) {
                return deferred.reject(missing_token);
            }
            if (!expected_response) {
                return deferred.reject({ detail: 'Unkown error.'});
            }
            // Check for the list of valid countries. It *should* be in the 
            // response. Double-check for sanity's sake.
            var valid_countries;
            try {
                valid_countries = _(data.errors).find(function(e) {
                    return e.source.pointer == '/data/attributes/country';
                }).meta.choice;
            } catch (e) {
                return deferred.fail(Error("Couldn't find a list of countries in the error response."));
            }

            return deferred.resolve({
                suggested_country: data.meta.suggested_country,
                valid_countries: valid_countries,
                password_requirements: data.meta.password_requirements
            });
        }
        return Api.ajax_v5({
            method: 'POST',
            url: Api.urls.auth_verify_signup(),
            headers: {
                'X-Electricimp-Token': decodeURIComponent(this.get('token'))
            },
            data: JSON.stringify({data: {'attributes': {'country':''}}})
        }).then(handle_success, handle_error);
    },
    /**
     * Request a forgotten password token via email
     */
    request_password_reset: function(email) {
        email = email || this.get('email');
        return Api.ajax({
            method: 'POST',
            headers: {
                'Content-type': 'application/json'
            },
            url: Api.urls.auth_request_password_reset(),
            // headers: {
            //     'X-Electricimp-Token': this.get('token')
            // },
            data: JSON.stringify({ id: email })
        });
    },
    /**
     * Reset a password using a token
     */
    reset_password: function(password, token) {
        password = password || this.get('password');
        token = token || this.get('token');
        return Api.ajax({
            method: 'POST',
            headers: {
                'Content-type': 'application/json'
            },
            url: Api.urls.auth_reset_password({ token: decodeURIComponent(token) }),
            data: JSON.stringify({ 
                password: password 
            })
        });
    }
});

